import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Page hero — used by Groupe, Ecosysteme, Projets, Impact, Actualites, Carrieres, Contact.
 * Dark navy background with copper eyebrow.
 */
export default function PageHero({ overline, title, titleItalic, subtitle, image }) {
  return (
    <section
      data-testid="page-hero"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-ekip-navy overflow-hidden"
    >
      {image && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25"
            style={{ backgroundImage: `url(${image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ekip-navy via-ekip-navy/95 to-ekip-navy/70" />
        </>
      )}
      {/* Faint diamond backdrop */}
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none">
        <div
          className="w-[600px] h-[600px] border border-ekip-gold"
          style={{ transform: "rotate(45deg)" }}
        />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="diamond" />
          <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
            {overline}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] text-white max-w-5xl"
        >
          {title}
          {titleItalic && (
            <>
              <br />
              <em className="italic font-light text-ekip-copper">{titleItalic}</em>
            </>
          )}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 max-w-2xl text-base md:text-lg font-body font-light text-white/80 leading-relaxed"
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </section>
  );
}
